import { Box, Button, Theme, useMediaQuery } from "@mui/material";
import { FundRecommendationResponse } from "@rentablo/api/recommender";
import { useTranslation } from "react-i18next";
import { ScrollableWithGradients } from "@rentablo/components";
import { PerformanceChartContainer } from "@features/recommender/components/PerformanceChartContainer/PerformanceChartContainer";
import { DetailCards } from "@features/recommender/components/DetailCard/DetailCard";
import { Holdings } from "@features/holdings/Holdings";
import { PerformanceNumbers, PerformanceAndTags } from "@features/performance";
import { useRecommendationDetails } from "../RecommendationDetailsProvider";
import { getCards } from "../utils";
import { HeaderSection } from "../components";
import { BasicDataSection } from "./components/BasicDataSection";
import { PolicySection } from "./components/PolicySection";
import { CostAndCashbackSection } from "./components/CostAndCashbackSection";
import { PerformanceGaugeSection } from "./components/PerformanceGaugeSection";
import { usePerformanceTagsData } from "./hooks/usePerformanceTagsData";

type Props = {
  onClose: () => void;
  onToggleWatchlist: (rec: FundRecommendationResponse) => void;
  isWatchlisted: boolean;
  isSavingWatchlist: boolean;
  onCompareClick: () => void;
};

export const InfoView = ({
  onClose,
  onToggleWatchlist,
  isWatchlisted,
  isSavingWatchlist,
  onCompareClick,
}: Props) => {
  const { t } = useTranslation();
  const { fundDetails, quote, recommendation } = useRecommendationDetails();
  const performanceTagsData = usePerformanceTagsData(recommendation);
  const cardsData = getCards(recommendation);
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const holdingsChartData = fundDetails?.breakdowns?.holding
    ?.map((holding) => ({
      name: holding.holdingName,
      y: holding.percent,
    }))
    ?.sort((a, b) => b.y - a.y);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      position="relative"
    >
      <HeaderSection onClose={onClose} />
      <ScrollableWithGradients flex={1} sx={{ pr: 2 }}>
        <PerformanceNumbers
          primaryValue={quote?.last ?? 0}
          secondaryValue={quote?.changePercent ?? 0}
          containerSx={{
            mb: 4,
            mt: 8,
          }}
        />
        <DetailCards cards={cardsData} isSmallerFontSize />
        <BasicDataSection />
        <PerformanceAndTags {...performanceTagsData} containerSx={{ mb: 10 }} />
        <PerformanceGaugeSection />
        <PerformanceChartContainer
          tickerSymbol={quote?.tickerSymbol}
          name={recommendation.fund.name}
        />
        <PolicySection />
        <Holdings
          chartData={holdingsChartData}
          title={t("recommender.detailsModal.holdings")}
        />
        <CostAndCashbackSection />
      </ScrollableWithGradients>
      <Box
        position="absolute"
        bottom={0}
        right={0}
        display="flex"
        flexDirection={isLessSm ? "column" : "row"}
        width="100%"
        justifyContent="flex-end"
        gap={4}
        zIndex={1000}
      >
        <Button
          variant="secondary"
          onClick={onCompareClick}
          size="medium"
          fullWidth={isLessSm}
        >
          {t("recommender.compare")}
        </Button>
        <Button
          variant={isWatchlisted ? "tertiary" : "primary"}
          disabled={isSavingWatchlist}
          size="medium"
          fullWidth={isLessSm}
          onClick={() => onToggleWatchlist(recommendation)}
          sx={{ minWidth: "fit-content" }}
        >
          {isWatchlisted
            ? t("recommender.removeFromWatchlist")
            : t("recommender.addToWatchlist")}
        </Button>
      </Box>
    </Box>
  );
};
